import React from 'react';
import { X } from 'lucide-react';
import QuantityCounter from './QuantityCounter';
import { CategoryPlaceholder } from '../../lib/consts/categoryPlaceholders';
import { getImageUrl } from '../../lib/helpers';
import { cantidadDespachada, notaPedidoMerma } from '../../lib/helpers/products';

/**
 * Club_cart row — una línea del carrito del POS.
 *
 * Contrapartida en código del componente de Figma (3204:25971), con sus dos
 * propiedades cruzadas (6 variantes):
 *
 *   Medida = Unidad      contador −/+        · total calculado
 *   Medida = Peso        gramos y euros TECLEABLES (los dos, enlazados)
 *   Medida = Peso fijo   gramos ya fijados, solo se puede quitar la línea
 *
 *   Property 1 = Default     con controles y con la X
 *   Property 1 = read only   sin controles y sin X (resumen de compra, historial,
 *                            y el bloque «también en Barra/Dispensario»)
 *
 * Rejilla del frame: 171 / 72 / 88 / 48. La primera celda va FILL —como manda
 * la norma de las tablas del sistema— para que las columnas de la derecha
 * queden alineadas entre filas aunque el panel cambie de ancho.
 *
 * Los dos campos de «Peso» se exponen por separado (`onWeightChange` /
 * `onPriceChange`) en vez de enlazarlos aquí dentro: el precio por gramo, el
 * descuento del socio y el de la línea los conoce el carrito, no la fila.
 */

/** Campo del frame: 72×24, valor en negrita a la izquierda y unidad apagada a la derecha. */
function Campo({ value, unidad, onChange, disabled, label }) {
  return (
    <div
      className={`flex h-6 w-[72px] items-center rounded-sm border border-[var(--border)] bg-[var(--background)] px-2 shadow-sm ${
        disabled ? 'opacity-60' : ''
      }`}
    >
      <input
        type="text"
        inputMode="decimal"
        aria-label={label}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        className="w-full min-w-0 bg-transparent text-xs font-bold tabular-nums text-[var(--foreground)] focus:outline-none"
      />
      <span className="ml-1 flex-shrink-0 text-xs text-[var(--muted-foreground)]">{unidad}</span>
    </div>
  );
}

export default function CartRow({
  item,
  /** 'unidad' | 'peso' | 'pesoFijo' */
  medida = 'unidad',
  readOnly = false,
  /** Cantidad en unidades (medida = unidad). */
  onQuantityChange,
  /** Gramos tecleados (medida = peso). Llega el texto en crudo. */
  onWeightChange,
  /** Euros tecleados (medida = peso). Llega el texto en crudo. */
  onPriceChange,
  onRemove,
  /**
   * Detalle extra bajo el precio unitario. El frame no lo contempla, pero el POS
   * cuelga aquí cosas que no se pueden perder: de qué ubicación sale la línea
   * (regla de oro de stock), el peso real de la báscula y el descuento manual.
   * Va como hueco y no como props sueltas para que la fila no tenga que saber
   * de ubicaciones ni de básculas.
   */
  extra = null,
  /**
   * Etiqueta accesible del campo de cantidad/gramos. Por defecto «Cantidad de X»
   * / «Gramos de X»; la Sala la cambia porque en su pantalla conviven la tarjeta
   * del producto (que ya se llama «Gramos de X») y esta línea.
   */
  labelCantidad = null,
  /**
   * El frame recorta el nombre a una línea. La Sala del club social decidió lo
   * contrario (#544, reporte CRUO): el nombre entero, envuelto por palabras.
   */
  nombreEntero = false,
  /**
   * Columna estrecha (la Sala del club social: 340 px al lado de la rejilla, no
   * un cajón de 420–528). Mismas celdas y mismo orden que el frame, sin la
   * miniatura y con anchos más cortos (64 / 72 / 32, sin los 16 de margen),
   * para que el nombre conserve ~130 px y no se parta letra a letra.
   */
  compacta = false,
  /** Controles apagados (la comanda está en vuelo): −/+, gramos, euros y la ✕. */
  disabled = false,
  /**
   * En «Peso», si los euros se teclean o solo se leen. La Sala del club social
   * (Yago, 20-09-2026) deja los gramos editables y el importe fijo: varía con
   * el peso, pero no se escribe.
   */
  importeEditable = true,
  className = '',
}) {
  const anchoCantidad = compacta ? 'w-[64px]' : 'w-[72px]';
  const anchoImporte = compacta ? 'w-[72px] pr-1' : 'w-[88px] pr-4';
  const anchoQuitar = compacta ? 'w-8' : 'w-12';
  const margenProducto = compacta ? 'pl-0' : 'pl-4';
  const nombre = item?.name || '';
  const etiquetaCantidad = labelCantidad || (medida === 'unidad' ? `Cantidad de ${nombre}` : `Gramos de ${nombre}`);
  const unidad = medida === 'unidad' ? 'u' : 'g';
  const precioUnitario = Number(item?.price) || 0;
  // Lo PEDIDO manda en el dinero; lo DESPACHADO (báscula) es lo que se enseña.
  const pedido = Number(item?.quantity) || 0;
  const cantidad = cantidadDespachada(item);
  const totalLinea = Number(item?.line_total ?? precioUnitario * pedido);
  const nota = notaPedidoMerma(item);
  const imagen = item?.image_path ? getImageUrl(item.image_path) : null;

  return (
    <div
      data-testid="cart-row"
      data-medida={medida}
      data-readonly={readOnly ? 'true' : 'false'}
      className={`flex min-h-[60px] items-start py-1 ${className}`}
    >
      {/* 1 · Producto (FILL). items-start: si la nota o el extra crecen, la miniatura
          se queda arriba con el nombre. Con items-center (antes) la foto bajaba al
          centro del bloque y la merma/almacén parecían desplazadas bajo ella
          (iPad, 05-09-2026). */}
      <div className={`flex min-w-0 flex-1 items-start gap-2 ${margenProducto}`}>
        {!compacta && (
          <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-md">
            {imagen ? (
              <img src={imagen} alt={nombre} className="h-full w-full object-cover" />
            ) : (
              <CategoryPlaceholder category={item?.category?.name} size="sm" />
            )}
          </div>
        )}
        <div className="min-w-0">
          <div className={`${nombreEntero ? 'break-words' : 'truncate'} text-xs font-semibold text-[var(--foreground)]`}>{nombre}</div>
          <div className="text-xs text-[var(--muted-foreground)]">
            €{precioUnitario.toFixed(2)} / {unidad}
          </div>
          {/* La nota pedido/merma vive AQUÍ y solo aquí: la fila es su único render.
              Bajo la cantidad (72px) se partía en tres renglones, y el POS la duplicaba
              por `extra` (staging, 05-09-2026). En el iPad el hueco del nombre es
              estrecho: cada trozo va nowrap para no cortar «merma +0.071 g», y a
              partir de `md` la nota entera cabe en una línea (panel a 33rem). */}
          {nota && (
            <div
              data-testid="cart-row-merma"
              className="text-[10px] tabular-nums text-[var(--muted-foreground)] md:whitespace-nowrap"
            >
              {nota.split(' · ').map((parte, i) => (
                <React.Fragment key={parte}>
                  {i > 0 ? ' · ' : null}
                  <span className="whitespace-nowrap">{parte}</span>
                </React.Fragment>
              ))}
            </div>
          )}
          {extra}
        </div>
      </div>

      {/* 2 · Cantidad. h-10 = la miniatura, para alinear el peso con el nombre
          aunque debajo crezcan merma / almacén / dto. */}
      <div className={`flex h-10 ${anchoCantidad} flex-shrink-0 items-center justify-center`}>
        {medida === 'unidad' && !readOnly && (
          <QuantityCounter
            value={cantidad}
            min={1}
            disabled={disabled}
            onChange={onQuantityChange}
            label={etiquetaCantidad}
          />
        )}
        {medida === 'unidad' && readOnly && (
          <span className="text-xs tabular-nums text-[var(--muted-foreground)]">x{cantidad}</span>
        )}
        {medida === 'peso' && !readOnly && (
          <Campo
            value={item?.weight_draft ?? String(pedido)}
            unidad="g"
            disabled={disabled}
            onChange={onWeightChange}
            label={etiquetaCantidad}
          />
        )}
        {(medida === 'pesoFijo' || (medida === 'peso' && readOnly)) && (
          <span className="text-xs tabular-nums text-[var(--muted-foreground)]">{cantidad} g</span>
        )}
      </div>

      {/* 3 · Importe */}
      <div className={`flex h-10 ${anchoImporte} flex-shrink-0 items-center justify-end`}>
        {medida === 'peso' && !readOnly && importeEditable ? (
          <Campo
            value={item?.price_draft ?? totalLinea.toFixed(2)}
            unidad="€"
            disabled={disabled}
            onChange={onPriceChange}
            label={`Importe de ${nombre}`}
          />
        ) : (
          <span className="text-xs font-semibold tabular-nums text-[var(--foreground)]">
            €{totalLinea.toFixed(2)}
          </span>
        )}
      </div>

      {/* 4 · Quitar */}
      <div className={`flex h-10 ${anchoQuitar} flex-shrink-0 items-center justify-center`}>
        {!readOnly && onRemove && (
          <button
            type="button"
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation();
              onRemove(item);
            }}
            aria-label={`Quitar ${nombre} del carrito`}
            className="rounded-sm p-1 text-[var(--muted-foreground)] transition-colors hover:text-[var(--destructive)] disabled:opacity-40"
          >
            <X size={16} strokeWidth={1.5} />
          </button>
        )}
      </div>
    </div>
  );
}
